import {
  PaymentPlan,
  PaymentPlanLimit,
} from '../models/payment-plan.model';

/**
 * Curated parent-facing highlights (not the full admin catalog).
 * Values still come from GET /payments/plans → limits.
 */
export const PLAN_FEATURE_LIMIT_KEYS = [
  'kids.max',
  'family.extended_seats',
  'ai.general.requests',
  'ai.kid.requests',
  'ai.kid_agent',
  'ai.speech_to_text.minutes',
  'duties.create',
  'duties.routines',
  'patrol.access',
  'xp.pool.credit.month',
  'xp.pool.carry_cap',
] as const;

export type PlanFeatureLimitKey = (typeof PLAN_FEATURE_LIMIT_KEYS)[number];

export interface PlanFeatureLine {
  key: string;
  /** Fully localized sentence for the row */
  text: string;
  state: 'disabled' | 'limited' | 'unlimited';
}

const LINE_KEY_PREFIX = 'app.components.upgradePlan.limitLines.';

const CAPABILITY_KEYS = new Set([
  'family.extended_invite',
  'patrol.access',
  'ai.kid_agent',
  'ai.speech_to_text',
  'duties.recurrence',
  'duties.family',
  'addons.purchase',
]);

export function findPlanLimit(
  plan: PaymentPlan | undefined,
  key: string,
): PaymentPlanLimit | undefined {
  return plan?.limits?.find((limit) => limit.key === key);
}

function resolveState(
  limit: PaymentPlanLimit,
): PlanFeatureLine['state'] {
  if (limit.state === 'unlimited' || limit.value < 0) return 'unlimited';
  if (limit.state === 'disabled' || limit.value === 0) return 'disabled';
  return 'limited';
}

function lineKey(scopeKey: string, state: PlanFeatureLine['state']): string {
  const base = `${LINE_KEY_PREFIX}${scopeKey.replace(/\./g, '_')}`;
  if (state === 'disabled') return `${base}.off`;
  if (state === 'unlimited') return `${base}.unlimited`;
  return `${base}.on`;
}

function formatNumericValue(
  limit: PaymentPlanLimit,
  t: (key: string, params?: Record<string, string | number>) => string,
): string {
  if (!limit.meter || limit.meter.period === 'none') {
    return t('app.components.upgradePlan.limitValues.count', {
      value: limit.value,
    });
  }

  const period = t(
    `app.components.upgradePlan.limitPeriods.${limit.meter.period}`,
  );
  const subject = t(
    `app.components.upgradePlan.limitSubjects.${limit.meter.subject}`,
  );

  if (limit.meter.subject === 'family' || limit.meter.subject === 'none') {
    return t('app.components.upgradePlan.limitValues.perPeriod', {
      value: limit.value,
      period,
    });
  }

  return t('app.components.upgradePlan.limitValues.perSubjectPeriod', {
    value: limit.value,
    subject,
    period,
  });
}

/**
 * Build curated, sentence-style feature lines from API plan.limits.
 */
export function buildPlanFeatureLines(
  plan: PaymentPlan | undefined,
  t: (key: string, params?: Record<string, string | number>) => string,
): PlanFeatureLine[] {
  if (!plan?.limits?.length) return [];

  const byKey = new Map(plan.limits.map((limit) => [limit.key, limit]));
  const lines: PlanFeatureLine[] = [];

  for (const key of PLAN_FEATURE_LIMIT_KEYS) {
    const limit = byKey.get(key);
    if (!limit) continue;

    const state = resolveState(limit);
    const params =
      state === 'limited'
        ? { value: limit.value }
        : undefined;

    lines.push({
      key,
      state,
      text: t(lineKey(key, state), params),
    });
  }

  return lines;
}

/** @deprecated kept for any callers expecting old signature */
export function formatPlanLimitValue(
  limit: PaymentPlanLimit,
  t: (key: string, params?: Record<string, string | number>) => string,
): string {
  const state = resolveState(limit);
  if (state === 'disabled') {
    return t('app.components.upgradePlan.limitValues.off');
  }
  if (state === 'unlimited') {
    return t('app.components.upgradePlan.limitValues.unlimited');
  }
  if (CAPABILITY_KEYS.has(limit.key)) {
    return t('app.components.upgradePlan.limitValues.on');
  }
  return formatNumericValue(limit, t);
}
